import React from "react";
import PropTypes from "prop-types";

import {
  APPOINTMENT_PAYMENT_STATUS,
  isEndStatus,
  isEndStatusCancelled,
} from "../helper/appointmentConstants";

/**
 * The payment banner at the top of Appointment details.
 *
 * Ported from bridge, which draws it from DocumentUpload.js:2841-2892 through
 * its shared <TopNote>. Same four sentences, same two faces, same conditions —
 * phr had none of them, so an appointment whose link was still unpaid, or which
 * had just been cancelled BECAUSE it went unpaid, said nothing about why.
 *
 * TWO FACES:
 *   · a soft one while the clock is running (payment INITIATED) — a 3px red
 *     outline over a 15% red wash, dark text;
 *   · a solid red one once the appointment has been cancelled for non-payment
 *     (a cancelled-family status AND payment LINK_EXPIRED), white on red.
 *
 * ONE DELIBERATE DEVIATION. Bridge interpolates the deadline unguarded —
 * `moment(appointment?.paymentDetails?.paymentLink?.expireAt)` — and
 * `moment(undefined)` is NOW, so a row that reaches this state without a
 * paymentLink prints the CURRENT time as the deadline and "within undefined
 * minutes". A wrong deadline on screen is worse than no deadline: an operator
 * can act on it. Here the time clause is dropped when there is no link to take
 * it from, and the sentence still says what is happening.
 */

/**
 * "07:30 PM" — bridge's moment format "hh:mm A": zero-padded 12-hour clock and
 * an UPPERCASE meridiem.
 *
 * Built from the date's own parts rather than toLocaleTimeString, which is what
 * phr's other expiry stamp uses: that API's meridiem case is decided by the
 * engine's ICU data, and it really does differ — the same instant came back
 * "07:30 PM" from Node and "07:30 pm" from Chrome while this was being written.
 * A banner that reads differently depending on the browser is not something to
 * leave to chance, and this is the one place phr must match bridge's casing.
 */
const timeOf = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  const meridiem = d.getHours() >= 12 ? "PM" : "AM";
  const hour = d.getHours() % 12 || 12;
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return `${pad(hour)}:${pad(d.getMinutes())} ${meridiem}`;
};

const PaymentNote = ({ appointment }) => {
  if (!appointment) return null;

  const paymentStatus = appointment.paymentStatusResponse;
  const link =
    (appointment.paymentDetails && appointment.paymentDetails.paymentLink) || null;

  /* "07:30 PM (i.e. within 20 minutes of payment link generation)", or "" when
     there is no link to read either half from — see the deviation above. */
  const deadline =
    link && link.expireAt && link.linkValidMins
      ? `${timeOf(link.expireAt)} (i.e. within ${link.linkValidMins} minutes of payment link generation)`
      : "";

  if (paymentStatus === APPOINTMENT_PAYMENT_STATUS.INITIATED) {
    /* Terminal appointments get the gentler sentence: there is nothing left to
       cancel, but the money is still owed. */
    const text = isEndStatus(appointment.status)
      ? deadline
        ? `Note: Payment is still pending. Please complete it before ${deadline}`
        : "Note: Payment is still pending."
      : deadline
      ? `Note: This appointment will be cancelled if payment is not received by ${deadline}`
      : "Note: This appointment will be cancelled if payment is not received in time.";
    return (
      <div className="ad-note" role="status">
        {text}
      </div>
    );
  }

  if (
    isEndStatusCancelled(appointment.status) &&
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED
  ) {
    /* Bridge switches on whether a link was ever generated, because "cancelled
       due to payment not received by <time>" is a false account of an
       appointment that never had a link to pay through. */
    const text =
      link && link.link && deadline
        ? `Note: This appointment is cancelled due to payment not received by ${deadline}`
        : "Note: This appointment is cancelled due to payment not received (Payment link was not generated)";
    return (
      <div className="ad-note is-solid" role="status">
        {text}
      </div>
    );
  }

  return null;
};

PaymentNote.propTypes = {
  /** The raw appointment row, as the filter/by-id read returns it. */
  appointment: PropTypes.shape({
    status: PropTypes.number,
    /** Server-computed, never stored — see APPOINTMENT_PAYMENT_STATUS. */
    paymentStatusResponse: PropTypes.number,
    paymentDetails: PropTypes.shape({
      paymentLink: PropTypes.shape({
        link: PropTypes.string,
        expireAt: PropTypes.string,
        linkValidMins: PropTypes.number,
      }),
    }),
  }),
};

export default PaymentNote;
